import { CHECKS_UPLOAD_PROGRESS } from '@common/constants/webSockets'
import { ActivityStatus } from '@common/interfaces/activity'
import { IFile } from '@common/interfaces/box'
import { ICheckInvoiceReviewData } from '@common/interfaces/checkRemittance'
import { IDeletedItems } from '@common/interfaces/client'
import {
  ICheckAccount,
  IWireTotals,
  IAggregationData,
  IWireDataAggregation,
  ICheckData,
  IChecksSummaryInfo,
  ICollectionInvoice,
  IChecksUploadProgress,
} from '@common/interfaces/collection'
import omit from 'lodash/omit'
import { combineReducers } from 'redux'
import { loadingArrayReducer } from '../../helpers/helpers'
import { createLoadingDataReducer } from '../../helpers/redux'
import { IAction, IActionWithParams } from '../apiMiddleware'
import * as actions from './actions'

export default combineReducers({
  loadingArray: loadingArrayReducer(
    [actions.prefix],
    [
      actions.LIST_MID_DAY_WIRES_NEW_REQUEST,
      actions.LIST_NEW_WIRES_REQUEST,
      actions.LIST_DELETED_WIRES_REQUEST,
      actions.LIST_MID_DAY_WIRES_DELETED_REQUEST,
      actions.LIST_MID_DAY_WIRES_TOTALS_REQUEST,
      actions.LIST_AGGREGATION_ROW_REQUEST,
      actions.EXPORT_AGGREGATION_REQUEST,
      actions.DELETE_AGGREGATION_ROW_REQUEST,
      actions.UPDATE_AGGREGATION_ROW_REQUEST,
      actions.SENT_DELETED_ITEM_REQUEST,
      actions.UNSENT_DELETED_ITEM_REQUEST,
      actions.CREATE_DELETED_ITEM_REQUEST,
      actions.UPDATE_DELETED_ITEM_REQUEST,
      actions.LIST_COLLECTION_INVOICES_REQUEST,
    ],
  ),
  checksFiles(state: IFile[] = [], action: IAction): IFile[] {
    switch (action.type) {
      case actions.LIST_CHECKS_SUCCESS:
      case actions.UPLOAD_CHECKS_SUCCESS: {
        const data = action.data as { files: IFile[] }
        return data.files
      }
      case actions.SAVE_CHECKS_SUCCESS:
        return []
      default:
        return state
    }
  },
  checksUploadProgress(
    state: Record<string, IChecksUploadProgress> = {},
    action: IActionWithParams<actions.IUploadChecksParams>,
  ): Record<string, IChecksUploadProgress> {
    switch (action.type) {
      case CHECKS_UPLOAD_PROGRESS: {
        const { activityId, stage, processed, total, fileCount } =
          action.data as IChecksUploadProgress
        return { ...state, [activityId]: { activityId, stage, processed, total, fileCount } }
      }
      // Cleared on REQUEST too, so an event that landed after a previous upload's reply is not shown.
      case actions.UPLOAD_CHECKS_REQUEST:
      case actions.UPLOAD_CHECKS_SUCCESS:
      case actions.UPLOAD_CHECKS_FAILURE: {
        const activityId = action.params?.activityId
        return activityId && state[activityId] ? omit(state, activityId) : state
      }
      default:
        return state
    }
  },
  checks: createLoadingDataReducer<ICheckData>(
    [actions.LIST_CHECKS_REQUEST],
    [actions.UPDATE_CHECK_REQUEST, actions.DELETE_CHECK_REQUEST, actions.UPDATE_CHECKS_REQUEST],
    true,
  ),
  checksSummaryInfo(state: IChecksSummaryInfo = null, action: IAction): IChecksSummaryInfo {
    switch (action.type) {
      case actions.GET_CHECKS_SUMMARY_INFO_SUCCESS:
        return action.data as IChecksSummaryInfo
      case actions.SAVE_CHECKS_SUCCESS:
        return null
      default:
        return state
    }
  },
  checkAccounts(state: ICheckAccount[] = [], action: IAction): ICheckAccount[] {
    switch (action.type) {
      case actions.LIST_CHECK_ACCOUNTS_SUCCESS:
        return action.data as ICheckAccount[]
      default:
        return state
    }
  },
  wiresFiles(state: IFile[] = [], action: IAction): IFile[] {
    switch (action.type) {
      case actions.LIST_WIRE_FILES_AND_STATUS_SUCCESS: {
        const data = action.data as { files: IFile[] }
        return data.files
      }
      case actions.SAVE_WIRES_SUCCESS:
      case actions.SAVE_MID_DAY_WIRES_SUCCESS:
        return []
      default:
        return state
    }
  },
  wiresData: createLoadingDataReducer<IWireDataAggregation>(
    [actions.LIST_MID_DAY_WIRES_NEW_REQUEST, actions.LIST_NEW_WIRES_REQUEST],
    [
      actions.UPDATE_WIRE_REQUEST,
      actions.DELETE_WIRE_REQUEST,
      actions.UPDATE_WIRES_REQUEST,
      actions.UPDATE_MID_DAY_WIRES_REQUEST,
      actions.DELETE_MID_DAY_WIRE_REQUEST,
      actions.UPDATE_MID_DAY_WIRE_REQUEST,
    ],
    true,
  ),
  wiresDataDeleted: createLoadingDataReducer<IWireDataAggregation>(
    [actions.LIST_DELETED_WIRES_REQUEST, actions.LIST_MID_DAY_WIRES_DELETED_REQUEST],
    [
      actions.UPDATE_WIRE_REQUEST,
      actions.UPDATE_WIRES_REQUEST,
      actions.RESTORE_WIRE_SUCCESS,
      actions.UPDATE_MID_DAY_WIRES_REQUEST,
      actions.RESTORE_MID_DAY_WIRE_REQUEST,
      actions.UPDATE_MID_DAY_WIRE_REQUEST,
    ],
    true,
  ),
  wiresDataTotals: createLoadingDataReducer<{ data: IWireTotals }>([
    actions.LIST_MID_DAY_WIRES_TOTALS_REQUEST,
  ]),
  collectionStatus(state: ActivityStatus | null = null, action: IAction): ActivityStatus | null {
    switch (action.type) {
      case actions.LIST_CHECKS_SUCCESS:
      case actions.LIST_WIRE_FILES_AND_STATUS_SUCCESS: {
        const data = action.data as { status: ActivityStatus }
        return data.status
      }
      default:
        return state
    }
  },
  aggregation: createLoadingDataReducer<IAggregationData>([
    actions.LIST_AGGREGATION_REQUEST,
    actions.LIST_AGGREGATION_ROW_REQUEST,
  ]),
  deletedItems: createLoadingDataReducer<IDeletedItems>(
    [actions.LIST_DELETED_ITEMS_REQUEST],
    [actions.SENT_DELETED_ITEM_SUCCESS],
    true,
  ),
  collectionInvoices: createLoadingDataReducer<ICollectionInvoice[]>([
    actions.LIST_COLLECTION_INVOICES_REQUEST,
  ]),
  checkInvoiceReview: createLoadingDataReducer<ICheckInvoiceReviewData>(
    [actions.GET_CHECK_INVOICE_REVIEW_REQUEST],
    [actions.RESOLVE_CHECK_REMITTANCE_REQUEST],
  ),
})
